import type { OnboardingCockpitStatus } from 'src/features/advisor-book-onboarding/constants/cockpit-url-state';
import { isNil } from 'es-toolkit';
import { blockedReasonLabelFor } from 'src/features/advisor-book-onboarding/constants/cockpit-status-copy';
import { formatLargeDollarAmount } from 'src/utils/formatting';

type BillablePctParams = {
  readonly est: number;
  readonly transferred: number;
  readonly status: OnboardingCockpitStatus;
};

export function billableTransferPct({ est, transferred, status }: BillablePctParams) {
  if (status === 'complete') {
    return 1;
  }
  if (est > 0) {
    return Math.min(1, Math.max(0, transferred / est));
  }
  return 0;
}

export function billableAssetsCaption({ est, transferred, status }: BillablePctParams) {
  if (transferred > 0) {
    return `${formatLargeDollarAmount(transferred)} transferred`;
  }
  if (status === 'complete' && est > 0) {
    return `${formatLargeDollarAmount(est)} transferred`;
  }
  return 'Not yet transferred';
}

export function blockedBannerTitle(blockedReason: string | null | undefined) {
  if (blockedReason === 'missing_client_info') {
    return 'Contact info incomplete';
  }
  if (!isNil(blockedReason) && blockedReason !== '') {
    return blockedReasonLabelFor(blockedReason);
  }
  return 'Blocked';
}

export function blockedBannerDescription(blockedReason: string | null | undefined) {
  if (blockedReason === 'missing_client_info') {
    return "Missing or invalid email / phone on one or more clients. Onboarding can't send until it's fixed.";
  }
  return 'Open the cockpit or contact ops if you need help unblocking this household.';
}

export function accountNumberLast4(accountNumber: string | null | undefined) {
  if (isNil(accountNumber) || accountNumber === '') {
    return null;
  }
  const digits = accountNumber.replace(/\D/g, '');
  if (digits.length < 4) {
    return null;
  }
  return digits.slice(-4);
}

export function accountStatusBadgeFor(status: OnboardingCockpitStatus) {
  switch (status) {
    case 'complete':
      return { label: 'Assets received', variant: 'primary-muted' as const, kind: 'complete' as const };
    case 'in_progress':
      return { label: 'Transferring', variant: 'info-muted' as const, kind: 'in_progress' as const };
    case 'awaiting_client':
    case 'ready_to_send':
    case 'confirm_account':
    case 'blocked':
      return { label: 'Awaiting forms', variant: 'outline' as const, kind: 'awaiting' as const };
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
